import React, { useCallback, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import {
  Box,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { FormattedMessage, injectIntl } from 'react-intl';
import ApiClient from '../loopback/apiClient';
import {
  fetchFullDigitalSurvey,
  fetchDigitalSubmissionsWithMetas,
  setSelectedDigitalsection,
  addOrUpdateDigitalSubmission,
  fetchInstituteDigitalSubmissions,
  setDigitalSubmissionMeta,
  fetchSurveySubmissionMeta,
  finalizeSurveySubmissionMeta,
  setSurveySubmissionMeta,
} from '../redux/actions';
import {
  selectDigitalCases,
  selectSelectedSection,
  selectDigitalSections,
  selectDigitalSubmissionByQuestionAndCase,
  selectDigitalSubmissionMetaBySection,
  selectNormalQuestionsDRT,
} from '../redux/reducers/SurveyDataReducer';
import { AggregatedSubmission, Enrollment, RootState } from '../redux/types';
import ReviewSubmissionTable from '../components/review/ReviewSubmissionTable';
import GenericDialog from '../components/GenericDialog';
import SubmissionAggregation from '../components/submissionAggregation/SubmissionAggregation';

const DigitalEvaluationDialogDRT: React.FC<Props> = ({
  trialId,
  enrollmentId,
  admin,
  sections,
  selectedSection,
  questions,
  cases,
  submissionsMapByQuestionAndCaseId,
  sectionSubmissionMeta,
  aggregatedSubmission,
  submissionMetas,
  fetchFullDigitalSurvey: dispatchFetchFullDigitalSurvey,
  fetchDigitalSubmissionsWithMetas: dispatchFetchDigitalSubmissionsWithMetas,
  setSelectedDigitalsection: dispatchSetSelectedDigitalSection,
  onClose,
}: Props) => {
  const [enrollment, setEnrollment] = useState<Enrollment>();

  /** Loads data in redux when the user is admin */
  const adminBootstrap = useCallback(() => {
    ApiClient.getDigitalSurveyByTrialId(Number(trialId))
      .then((surveys) => surveys.filter((ds) => !ds.exercise))
      .then((selectedSurvey) => dispatchFetchFullDigitalSurvey(selectedSurvey[0].id));
    ApiClient.getEnrollments<Enrollment>(Number(trialId), { id: Number(enrollmentId), status: 'approved' }, null).then((e) => {
      const enrollmentObj = e[0];
      setEnrollment(enrollmentObj);
      dispatchFetchDigitalSubmissionsWithMetas(enrollmentObj.id);
    });
  }, [
    trialId,
    enrollmentId,
    dispatchFetchFullDigitalSurvey,
    dispatchFetchDigitalSubmissionsWithMetas,
  ]);

  /** Loads data in redux when user is not admin */
  // const userBootstrap = useCallback(() => {
  //   ApiClient.getDigitalSurveyByTrialId(Number(trialId))
  //     .then((surveys) => surveys.filter((ds) => !ds.exercise))
  //     .then((selectedSurvey) => dispatchFetchFullDigitalSurvey(selectedSurvey[0].id));
  //   dispatchFetchSurveySubmissionMeta(enrollmentId);
  //   dispatchFetchInstituteDigitalSubmissions(trialId);
  //   ApiClient.findOrCreateSubmissionMetaForAllSections(trialId)
  //     .then((dsms) => dispatchSetDigitalSubmissionMeta(dsms));
  // }, [
  //   trialId,
  //   enrollmentId,
  //   dispatchFetchFullDigitalSurvey,
  //   dispatchFetchSurveySubmissionMeta,
  //   dispatchFetchInstituteDigitalSubmissions,
  //   dispatchSetDigitalSubmissionMeta,
  // ]);

  const getAnsweredSections = useCallback(() => {
    const userAnsweredSectionIds = Object.values(submissionMetas)
      .map((dsm) => dsm.selectedSectionId);
    const userAnsweredSections = sections.filter((sec) => userAnsweredSectionIds.includes(sec.id));
    return userAnsweredSections;
  }, [
    sections,
    submissionMetas,
  ]);

  useEffect(() => {
    if (admin) {
      adminBootstrap();
    }
    // else {
    //   userBootstrap();
    // }
  }, [
    admin,
    adminBootstrap,
    // userBootstrap,
  ]);

  useEffect(() => {
    if (!selectedSection && getAnsweredSections().length > 0) {
      dispatchSetSelectedDigitalSection(getAnsweredSections()[0].id);
    }
  }, [selectedSection, dispatchSetSelectedDigitalSection, sections, getAnsweredSections]);

  const handleReviewTabSwitch = (event: React.SyntheticEvent, newValue: number) => {
    dispatchSetSelectedDigitalSection(newValue);
  };

  return (
    <GenericDialog
      isOpen
      maxWidth="xl"
      onRequestClose={onClose}
    >
      <Box sx={{ overflow: 'auto' }}>
        <Typography variant="h5" sx={{ mx: 2, mt: 2 }}>
          <FormattedMessage id="enrollmentsmanager.detailedEvaluation" values={{ trial: trialId }} />
        </Typography>
        {
          enrollment && (
            <Typography sx={{ mx: 2 }}>{`Enrollment ID: ${enrollment?.id}`}</Typography>
          )
        }
        <Typography sx={{ mx: 2 }}>{`Submission Finalized: ${!!sectionSubmissionMeta?.submissionFinalized}`}</Typography>
        {
          selectedSection && (
            <Box sx={{ mt: 4 }}>
              <Tabs
                value={selectedSection?.id}
                onChange={handleReviewTabSwitch}
                variant="scrollable"
                scrollButtons
              >
                {
                  getAnsweredSections().map((s) => <Tab label={s.name} key={s.id} value={s.id} />)
                }
              </Tabs>
            </Box>
          )
        }
        <Box sx={{ height: '100%', p: 2 }}>
          <ReviewSubmissionTable
            digitalCases={cases}
            digitalQuestions={questions}
            submissionsMapByQuestionAndCaseId={submissionsMapByQuestionAndCaseId}
            showLegend
            viewOnly
            mode="auswertung"
          />
        </Box>
        <Box sx={{
          my: 2, py: 2, px: 4, backgroundColor: 'grey.100',
        }}
        >
          <Box display="flex">
            <Box flexGrow={0.4}>
              <Typography fontWeight={500}>Evaluated Scores</Typography>
              <Typography>
                {`Section Score: ${sectionSubmissionMeta?.acquiredScore}`}
              </Typography>
              {/* <Typography>
                {`Overall Score: ${surveySubmissionMeta?.acquiredScore}`}
              </Typography> */}
            </Box>
            {/* <Box>
              <Typography fontWeight={500}>Reviewed Scores</Typography>
              <Typography>
                {`Section Review Score: ${sectionSubmissionMeta?.reviewScore}`}
              </Typography>
              <Typography>
                {`Overall Review Score: ${surveySubmissionMeta?.reviewScore}`}
              </Typography>
            </Box> */}
          </Box>
        </Box>
        {
          aggregatedSubmission && (
            <Box sx={{ p: 2 }}>
              <Typography variant="h6">Submission Aggregations</Typography>
              <SubmissionAggregation
                digitalCases={cases}
                digitalQuestions={questions}
                selectedSectionId={selectedSection?.id}
                aggregatedSubmission={aggregatedSubmission?.payload}
              />
            </Box>
          )
        }
      </Box>
    </GenericDialog>
  );
};

type AdminProps = {
  trialId: number;
  enrollmentId: number;
  aggregatedSubmission?: AggregatedSubmission;
  admin: true;
};

type UserProps = {
  trialId: number;
  enrollmentId: number;
  aggregatedSubmission?: AggregatedSubmission;
  admin: false;
};

const mapStateToProps = (state: RootState, ownProps: AdminProps | UserProps) => ({
  trialId: ownProps.trialId,
  enrollmentId: ownProps.enrollmentId,
  aggregatedSubmission: ownProps.aggregatedSubmission,
  admin: ownProps.admin,
  survey: state.SurveyData.survey,
  cases: selectDigitalCases(state),
  questions: selectNormalQuestionsDRT(state),
  selectedSection: selectSelectedSection(state),
  sections: selectDigitalSections(state),
  submissionsMapByQuestionAndCaseId: selectDigitalSubmissionByQuestionAndCase(state),
  sectionSubmissionMeta: selectDigitalSubmissionMetaBySection(state),
  surveySubmissionMeta: state.SurveyData.surveySubmissionMeta,
  submissionMetas: state.SurveyData.digitalSubmissionMeta.byId,
});

const dispatchProps = {
  fetchFullDigitalSurvey,
  fetchDigitalSubmissionsWithMetas,
  setSelectedDigitalsection,
  addOrUpdateDigitalSubmission,
  fetchInstituteDigitalSubmissions,
  setDigitalSubmissionMeta,
  fetchSurveySubmissionMeta,
  finalizeSurveySubmissionMeta,
  setSurveySubmissionMeta,
};
const connector = connect(mapStateToProps, dispatchProps);
type Props = {
  onClose: () => void;
} & ConnectedProps<typeof connector>;

export default injectIntl(connector(DigitalEvaluationDialogDRT));
